import React, { Fragment, useEffect, useMemo, useState } from 'react';
import RightSidebar from 'components/right-sidebar';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import BottomNavigation from 'components/bottom-navigation';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { SuccessModal } from 'components/base/success-modal';
import PageHeaderText from 'components/base/page-header-text';
import useCreateBlogContext from 'contexts/create-blog.context';
import { getBlogDetailsNav } from './utils';
import { cleanObject, getWordsCount } from 'utils/functions';
import { ROUTE_KEYS } from 'utils/constants';

const CreateBlogContent = () => {
  const { setRightSection } = useDashboardContext();
  const { createBlogForm, disabled, setDisabled, setWordsCount, wordsCount, setCreateBlogForm } =
    useCreateBlogContext();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [editing, setEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const blogDetailsNav = getBlogDetailsNav(location.search);
  const blogId = searchParams.get('blog_id');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');

  useEffect(() => {
    const count = getWordsCount(createBlogForm.body);
    setWordsCount(count);
  }, [createBlogForm.body]);

  useEffect(() => {
    setRightSection(
      <RightSidebar disabled={disabled && !createBlogForm.is_draft} list={blogDetailsNav} />
    );
    return () => {
      setRightSection(null);
    };
  }, [disabled]);

  const handleConfirmForm = async (saveDraft: boolean) => {
    setLoading(true);

    try {
      const payload = {
        ...createBlogForm,
        is_draft: saveDraft,
        pinned: createBlogForm.pinPost,
        tags: createBlogForm?.tags?.map(({ name, id }) => {
          return { name, id };
        })
      };
      if (!blogId) {
        await apiService.post('blog', {
          data: cleanObject(payload)
        });
        if (saveDraft) {
          setMessage('Draft saved');
        } else {
          setMessage('Blog created');
        }
      } else {
        await apiService.patch(`blog/${blogId}`, {
          data: payload
        });
        if (saveDraft) {
          setMessage('Draft saved');
        } else {
          setMessage('Blog updated');
        }
      }
      setCreateBlogForm({ ...createBlogForm, is_draft: saveDraft });
      setShowSuccess(true);
    } catch {
      toast(
        <ToastMessage
          type='error'
          message={`There was a problem ${blogId ? 'editing' : 'creating'} blog `}
        />
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      pathname.includes('body') &&
      (!createBlogForm.body || !createBlogForm.title || wordsCount < 100)
    ) {
      setDisabled(true);
    } else if (
      pathname.includes('create-blog-image') &&
      (createBlogForm.image?.length === 0 || createBlogForm.tags.length === 0)
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [createBlogForm, pathname, wordsCount]);

  const pageTitle = useMemo(() => {
    return !blogId ? 'New Blog Post' : blogId && editing ? ' Edit Blog Post' : 'Preview Blog Post';
  }, [blogId, editing]);

  return (
    <Fragment>
      <PageHeaderText title={pageTitle} description='Add a new article'>
        {blogId && !editing && (
          <button
            className='border border-[#E7E8E9] shadow text-[#5C5F64] w-[86px] h-[40px] py-2 rounded-[8px]'
            onClick={() => {
              setEditing(!editing);
            }}>
            {'Edit'}
          </button>
        )}
      </PageHeaderText>
      <div className={`${blogId && !editing && 'pointer-events-none cursor-not-allowed'}`}>
        <Outlet />
      </div>
      {blogId && !editing ? null : (
        <BottomNavigation
          removeAllNextSteps={!!blogId}
          onConfirmText={createBlogForm.is_draft ? 'Edit ' : 'Confirm'}
          secondaryBtn={
            <Button
              onClick={() => {
                if (createBlogForm.is_draft) return;
                handleConfirmForm(true);
              }}
              className={`${createBlogForm.is_draft && 'cursor-not-allowed opacity-50'} text-red-5`}
              color='transparent'
              variant='block'>
              {'Save as draft'}
            </Button>
          }
          loading={loading}
          list={blogId ? blogDetailsNav.splice(-1, 1) : blogDetailsNav}
          onConfirm={() => handleConfirmForm(createBlogForm.is_draft)}
          disabled={disabled && !createBlogForm.is_draft}
        />
      )}
      <SuccessModal
        link='/blogs'
        linkText='Back to Blog'
        onClose={() =>
          navigate(`${ROUTE_KEYS.BLOGS}?status=${createBlogForm.is_draft ? 'drafts' : 'published'}`)
        }
        show={showSuccess}>
        <div>
          <p>{message} successfully</p>
        </div>
      </SuccessModal>
    </Fragment>
  );
};

export default CreateBlogContent;
